import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Shared chrome for the PA surfaces' tables.
 *
 * Class constants rather than a `<DataTable>` component, following
 * `components/tasks/board-chrome.tsx`. The six tables in this pass differ in
 * column count, alignment, row height and what a row does when clicked; a
 * component general enough to cover all six would take more configuration than
 * the markup it replaced. What must not drift is the *chrome* — edge colours,
 * header type, row separators, hover — so that is what gets shared.
 *
 * Two conventions worth knowing before you use these:
 *
 *  - The grid template goes in `style`, never an arbitrary Tailwind class. The
 *    scanner only sees class strings it can read literally, so a template built
 *    at runtime would compile to nothing.
 *  - The header sits at `px-[19px]` against the rows' `px-[18px]`. The extra
 *    pixel pays for the card's 1px border, which the header is inside of and
 *    the rows' backgrounds are not, so labels land on their column's left edge.
 *
 * Below 1240px the grid is abandoned for a wrapping flex row — the same
 * fallback `task-list-view.tsx` uses — and the header row hides, because column
 * labels above wrapped content point at nothing.
 */

/** The card a table sits in. Pass `overflow-hidden` only if nothing must escape. */
export const TABLE_CARD_CLASS = "rounded-lg border border-border-subtle bg-card";

/** Column labels: 11px/600/0.04em, sentence case. Desktop only. */
export const TABLE_HEAD_CLASS = cn(
  "hidden border-b border-border-subtle px-[19px] py-2.5",
  "text-[11px] font-semibold tracking-[0.04em] text-muted-foreground",
  "min-[1240px]:grid",
);

/** A body row. Compose with a height class — rows vary from 38px to 60px. */
export const TABLE_ROW_CLASS = cn(
  "flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border-faint px-[18px] py-2.5",
  "transition-colors last:border-b-0 hover:bg-muted/40",
  "min-[1240px]:grid min-[1240px]:gap-y-0 min-[1240px]:py-0",
);

/** A cell that only earns its space at desktop width. */
export const TABLE_CELL_CLASS = "hidden min-[1240px]:block";

/** The column gap shared by header and rows. */
export const TABLE_GAP_CLASS = "min-[1240px]:gap-x-3.5";

/** A step-back/step-forward control for a paged table. Square and quiet — a
 * pager is navigation, not an action, and shouldn't out-weigh the rows. */
export const PAGER_BUTTON_CLASS = cn(
  "flex h-6 w-6 items-center justify-center rounded-md border border-border-subtle",
  "text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
  "disabled:pointer-events-none disabled:opacity-40",
);

/** The "3 / 12" between a pair of pager buttons. */
export const PAGER_COUNT_CLASS =
  "text-[11.5px] font-medium tabular-nums text-muted-foreground";

export function gridStyle(template: string): CSSProperties {
  return { gridTemplateColumns: template };
}

/**
 * The month/section divider inside a table.
 *
 * Gives a long run of near-identical rows a reason to be scanned. Static for
 * now; if it ever collapses, reuse the `lib/collapsed-sections` helper rather
 * than growing a second idiom.
 */
export function TableGroupRow({
  label,
  note,
  trailing,
  className,
}: {
  label: ReactNode;
  note?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 border-b border-border-subtle border-t border-t-border-faint",
        "bg-muted/40 px-[18px] py-1.5 min-[1240px]:h-8 min-[1240px]:py-0",
        className,
      )}
    >
      <span className="text-[11.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
        {label}
      </span>
      {note ? (
        <span className="truncate text-[11.5px] text-muted-foreground/70">
          {note}
        </span>
      ) : null}
      {trailing ? (
        <span className="ml-auto text-[12px] font-bold tabular-nums text-slate-600 dark:text-slate-300">
          {trailing}
        </span>
      ) : null}
    </div>
  );
}

/** Empty state, at the one size all these pages now share. */
export function TableEmpty({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        {icon}
      </div>
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      {children ? (
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{children}</p>
      ) : null}
    </div>
  );
}

/** Loading shape: the header rule plus a few row-height blocks. */
export function TableSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="px-[18px] py-2.5">
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={index}
          className="h-[42px] animate-pulse border-b border-border-faint last:border-b-0"
        >
          <div className="mt-3 h-4 w-full max-w-[520px] rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}
